import { useNavigate } from 'react-router-dom'
import { cn } from '../../utils/cn'
import { getLimitPercent } from '../../hooks/usePlanLimits'

interface PlanLimitBarProps {
  label: string
  used: number
  max: number
  className?: string
}

export function PlanLimitBar({ label, used, max, className }: PlanLimitBarProps) {
  const navigate = useNavigate()
  const unlimited = max === -1
  const percent = getLimitPercent(used, max)
  const isNearLimit = !unlimited && percent >= 80
  const isAtLimit = !unlimited && used >= max

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">{label}</span>
        <span className={cn('text-xs font-medium', isAtLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-gray-500')}>
          {unlimited ? `${used} (unlimited)` : `${used} of ${max} used`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-brand-500',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {isAtLimit && (
        <button
          onClick={() => navigate('/subscription')}
          className="text-xs text-brand-600 hover:underline font-medium"
        >
          Upgrade your plan to add more →
        </button>
      )}
    </div>
  )
}

interface UpgradeLimitModalProps {
  resource: string
  current: number
  limit: number
  onClose: () => void
}

export function UpgradeLimitModal({ resource, current, limit, onClose }: UpgradeLimitModalProps) {
  const navigate = useNavigate()

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center">
        <div className="h-12 w-12 rounded-2xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🚀</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Plan Limit Reached</h3>
        <p className="text-gray-600 text-sm mb-4">
          You have reached the limit of <strong>{limit}</strong> {resource} on your current plan.
          You currently have <strong>{current}</strong>.
        </p>
        <p className="text-gray-500 text-sm mb-6">
          Upgrade your plan to add more {resource}.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Maybe later</button>
          <button
            onClick={() => { onClose(); navigate('/subscription') }}
            className="btn-primary flex-1"
          >
            View Plans
          </button>
        </div>
      </div>
    </div>
  )
}
